import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';
import _ from 'underscore';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { UserErrorCode } from '~commonInfra/utils/ErrorCodes';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';

import { getAppStore } from '../../../../bootstrap/redux/store/AppStore';
import { Messages } from '../../../../codegen/Localize';
import { openReactActionDialog } from '../../../../commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '../../../../commonInfra/dialogs/style/TabDialogWidth';
import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import { ServerSettingsUnauthenticated } from '../../../../settings/services/ServerSettingsUnauthenticated';
import { DefaultSiteRole } from '../../../services/resources/SiteRoleOptions';
import { SiteRoles } from '../../../users/serverApi/types/ISiteRole';
import {
  CreateGroupActionDialogProps,
  CreateGroupDialogBody,
  CreateGroupDialogProps, CreateGroupDialogState
} from '../../components/CreateGroupDialog';
import { Groups } from '../../Groups';
import { GrantLicenseModes } from '../../serverApi/requests/SyncActiveDirectoryGroupRequest';


const initialBodyProps: CreateGroupDialogProps = {};

const initialDialogState: CreateGroupDialogState = {
  groupNameErrorMessage: undefined,
  groupNameText: '',
  grantLicenseMode: undefined,
  isGroupNameTextValid: true,
  minimumSiteRole: DefaultSiteRole,
  externalUserEnabled: false // TODO check if this should depend on the site
};

export function isSubmitEnabled(state: CreateGroupDialogState): boolean {
  return !_.isEmpty(state.groupNameText) && state.isGroupNameTextValid && !state.groupNameErrorMessage;
}

export function showCreateGroupDialog(): Promise<IResult> {
  const activeDirectoryEnabled = ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory();
  const title = activeDirectoryEnabled ? Messages.createLocalGroupTitle() : Messages.createGroupTitle();
  const actionDisabled = (props: CreateGroupDialogProps, state: CreateGroupDialogState) => {
    return !isSubmitEnabled(state);
  };

  const dialogInstance = openReactActionDialog<CreateGroupDialogProps, CreateGroupDialogState, IResult>({
    actionName: Messages.createGroupDialogSubmit(),
    action: createGroup,
    actionDisabled,
    bodyComponent: CreateGroupDialogBody,
    dialogWidth: TabDialogWidth.width650px,
    dismissDialogOnActionFailed: false,
    initialBodyProps,
    initialDialogState,
    title,
  });

  return Promise.resolve(dialogInstance.result);
}

async function createGroup(
  props: CreateGroupActionDialogProps,
  dialogState: CreateGroupDialogState,
  updateDialogState: React.Dispatch<MergeOrUpdateStateAction<CreateGroupDialogState>>): Promise<IResult> {

  const activeDirectoryEnabled = ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory();
  const { groupNameText, grantLicenseMode, minimumSiteRole, externalUserEnabled } = dialogState;

  if (!activeDirectoryEnabled && grantLicenseMode === GrantLicenseModes.ON_SYNC) {
    updateDialogState({ grantLicenseMode: undefined, minimumSiteRole: SiteRoles.Unlicensed });
  }
  const result = await Groups.createGroup(groupNameText, grantLicenseMode, grantLicenseMode ? minimumSiteRole : undefined, externalUserEnabled ? externalUserEnabled : undefined);

  return notify(updateDialogState, result, groupNameText);
}

function notify(
  updateDialogState: React.Dispatch<MergeOrUpdateStateAction<CreateGroupDialogState>>,
  result: IResult,
  groupName: string) {
  if (!!result.errors && result.errors.length > 0) {
    const errorMsg: string = errorMessageFor(result.errors[0].code, groupName);
    if (errorMsg) {
      // update dialog to show error message
      updateDialogState({ groupNameErrorMessage: errorMsg, isGroupNameTextValid: false });
      return Promise.reject();
    } else {
      ToasterService.instance.pop({ type: ToastType.Error, text: Messages.createGroupErrorMsg() });
      return Promise.resolve(result);
    }
  } else {
    getAppStore().dispatch(requestSpaceContents());
    ToasterService.instance.pop({ type: ToastType.Success, text: Messages.createGroupSuccessMsg() });
    return Promise.resolve(result);
  }
}

function errorMessageFor(errorCode: number, groupName: string) {
  switch (errorCode) {
    case UserErrorCode.UserGroupInvalidName:
      return Messages.errorUserGroupInvalidName();
    case UserErrorCode.UserGroupWithNameAlreadyExists:
      return Messages.errorUserGroupWithNameAlreadyExists();
    case UserErrorCode.UserAllUsersGroupAlreadyExists:
      return Messages.errorUserGroupWithNameAlreadyExists();
    default:
      return undefined;
  }
}
